import { useRef, useState } from "react";
import clsx from "clsx";
import { MoreVertical } from "lucide-react";
import { AnchoredPopover } from "components/common/AnchoredPopover";
import type { PlanRowActionsMenuProps } from "./types";

const ESTIMATED_MENU_ITEM_HEIGHT = 40;
const MENU_VERTICAL_PADDING = 8;

export const PlanRowActionsMenu = ({
  triggerTitle,
  items,
}: PlanRowActionsMenuProps) => {
  const anchorRef = useRef<HTMLSpanElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen((previous) => !previous);
  };

  return (
    <>
      <span
        ref={anchorRef}
        role="button"
        tabIndex={0}
        title={triggerTitle}
        onClick={(event) => {
          // preventDefault: the trigger can sit inside a link row, whose navigation it must not start.
          event.preventDefault();
          event.stopPropagation();
          toggleMenu();
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.stopPropagation();
            toggleMenu();
          }
        }}
        className={clsx(
          "flex-shrink-0 p-1 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 mt-0.5 opacity-0 group-hover:opacity-100 transition-all",
          isOpen && "opacity-100 text-blue-500 bg-blue-50",
        )}
      >
        <MoreVertical size={14} />
      </span>
      <AnchoredPopover
        isOpen={isOpen}
        anchorRef={anchorRef}
        onClose={() => setIsOpen(false)}
        align="right"
        estimatedHeight={
          items.length * ESTIMATED_MENU_ITEM_HEIGHT + MENU_VERTICAL_PADDING
        }
      >
        <div className="w-44 rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          {items.map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                item.onSelect();
                setIsOpen(false);
              }}
              className={clsx(
                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50",
                item.isDestructive ? "text-red-600" : "text-gray-700",
              )}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      </AnchoredPopover>
    </>
  );
};
